"use client"

import { TypographyH3, TypographySmall } from '../../../shared/components/ui/typography'
import React, { ReactNode, useState } from 'react'
import { ChevronDown, ChevronUp, Check } from 'lucide-react'
import Link from 'next/link'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../shared/components/ui/dropdown-menu'
import { HOMEOWNERS_ROUTES } from '../../../shared/constants/pages-routes'
import { useIsMobile } from '@/shared/hooks/use-mobile'
import { cn } from '@/shared/lib/utils'

type ProfileStatCardWithDropdownProps = {
    title: string,
    value?: string,
    icon: ReactNode,
    dropdownItems?: Array<{ 
        id: number; 
        workScope?: string; 
        workScopeDisplay?: string | null; 
        fullname?: string;
        email?: string;
        phone?: string;
        completed?: boolean 
    }>,
}

function ProfileStatCardWithDropdown(props: ProfileStatCardWithDropdownProps) {
    const [isOpen, setIsOpen] = useState(false)
    const isMobile = useIsMobile()
    const hasDropdown = props.dropdownItems && props.dropdownItems.length > 0

    const cardContent = (
        <>
            <div className='flex flex-col justify-between flex-1 min-w-0'>
                <TypographySmall className='text-muted-foreground text-sm'>{props.title}</TypographySmall>
                {props.value && <TypographyH3 className='text-xl'>{props.value}</TypographyH3>}
            </div>
            <div className='flex items-center gap-2 flex-shrink-0'>
                <div className='bg-primary/10 rounded-full p-3 w-12 aspect-square text-primary self-center flex items-center justify-center flex-shrink-0'>
                    {props.icon}
                </div>
                {hasDropdown && (
                    <div className='text-muted-foreground flex items-center flex-shrink-0'>
                        {isOpen ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
                    </div>
                )}
            </div>
        </>
    )

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            {isMobile && hasDropdown ? (
                <div className='border rounded px-3 py-2 w-full flex justify-between gap-5 overflow-hidden'>
                    <div className='flex flex-col justify-between flex-1 min-w-0'>
                        <TypographySmall className='text-muted-foreground text-sm'>{props.title}</TypographySmall>
                        {props.value && <TypographyH3 className='text-xl'>{props.value}</TypographyH3>}
                    </div>
                    <div className='flex items-center gap-2 flex-shrink-0'>
                        <div className='bg-primary/10 rounded-full p-3 w-12 aspect-square text-primary self-center flex items-center justify-center flex-shrink-0'>
                            {props.icon}
                        </div>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                className={cn(
                                    'text-muted-foreground flex items-center justify-center p-2 -m-2 rounded',
                                    'hover:bg-muted/50 hover:text-foreground transition-colors touch-manipulation'
                                )}
                                aria-label={`Expand ${props.title}`}
                            >
                                {isOpen ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
                            </button>
                        </DropdownMenuTrigger>
                    </div>
                </div>
            ) : hasDropdown ? (
                <DropdownMenuTrigger asChild>
                    <div className='border rounded px-3 py-2 w-full flex justify-between gap-5 cursor-pointer hover:bg-muted/50 transition-colors overflow-hidden'>
                        {cardContent}
                    </div>
                </DropdownMenuTrigger>
            ) : (
                <div className='border rounded px-3 py-2 w-full flex justify-between gap-5 overflow-hidden'>
                    {cardContent}
                </div>
            )}
            {hasDropdown && (
                <DropdownMenuContent align="end" className="w-56 max-h-96 overflow-y-auto">
                    {(props.dropdownItems ?? []).map((item) => {
                        // Check if this is a homeowner (has fullname) or work scope
                        const isHomeowner = !!item.fullname
                        const displayText = isHomeowner 
                            ? item.fullname 
                            : (item.workScopeDisplay || item.workScope || 'Unknown')
                        
                        // If it's a homeowner, wrap in a Link
                        if (isHomeowner) {
                            return (
                                <DropdownMenuItem key={item.id} asChild>
                                    <Link 
                                        href={HOMEOWNERS_ROUTES.details(item.id)}
                                        className="flex items-center gap-3 w-full cursor-pointer"
                                    >
                                        <span className="flex-1">{displayText}</span>
                                    </Link>
                                </DropdownMenuItem>
                            )
                        }
                        
                        // Otherwise, it's a work scope
                        return (
                            <DropdownMenuItem key={item.id} className="cursor-default">
                                <div className="flex items-center gap-3 w-full">
                                    <div className={`flex items-center justify-center w-5 h-5 border-2 rounded flex-shrink-0 ${
                                        item.completed 
                                            ? 'bg-primary border-primary' 
                                            : 'border-muted-foreground/30'
                                    }`}>
                                        {item.completed && (
                                            <Check className="h-3.5 w-3.5 text-primary-foreground" />
                                        )}
                                    </div>
                                    <span className="flex-1">{displayText}</span>
                                </div>
                            </DropdownMenuItem>
                        )
                    })}
                </DropdownMenuContent>
            )}
        </DropdownMenu>
    )
}

export default ProfileStatCardWithDropdown

